import { useTranslation } from "react-i18next";
import { Truck } from "lucide-react";
import { formatPrice, type Currency, type Locale, type Product, type ProductContent } from "@nh/shared";
import { numberLocaleOf, type ResolvedPrice } from "../shop.catalog";
import { ProductDatasheetButton } from "./ProductDatasheetButton";
import { ProductFacts } from "./ProductFacts";
import { ProductPriceTag } from "./ProductPriceTag";
import { ProductPurchase } from "./ProductPurchase";
import { ProductStockBadge } from "./ProductStockBadge";

export interface ProductPurchasePanelProps {
  product: Product;
  content: ProductContent;
  price: ResolvedPrice | null;
  currency: Currency;
  locale: Locale;
}

/**
 * La tarjeta de compra, en la columna derecha junto a "Sobre el
 * producto" —donde está en producción—. Lo que suma el rediseño
 * (28-ago): el precio de socio con lo que se ahorra, los PV, el aviso
 * de envío y la tarjeta como vidrio; la portada a sangre de arriba se
 * quedó tal cual (Anahí: "el header debe quedar igual").
 */
export function ProductPurchasePanel({ product, content, price, currency, locale }: ProductPurchasePanelProps) {
  const { t } = useTranslation();
  const numberLocale = numberLocaleOf(locale);
  const memberCents = price ? product.memberPrices?.[price.currency] : undefined;
  const memberSaving =
    price && memberCents !== undefined && memberCents < price.cents ? price.cents - memberCents : 0;

  return (
    <aside className="lg:col-start-2 lg:row-start-1">
      <div className="grid gap-5 lg:sticky lg:top-28">
        <div className="grid gap-4 rounded-[28px] border border-ds-border bg-white/85 p-5 shadow-ds-card backdrop-blur-xl sm:p-6">
          <div className="flex items-start justify-between gap-4">
            <ProductPriceTag price={price} currency={currency} locale={locale} />
            <div className="flex flex-col items-end gap-2">
              <ProductStockBadge stock={product.stock} />
              {product.volumePoints !== undefined && product.volumePoints > 0 && (
                <span className="rounded-full border border-ds-border bg-white px-2.5 py-1 font-mono text-[11px] font-semibold text-ds-violet-pop">
                  {t("{{n}} PV", { n: product.volumePoints })}
                </span>
              )}
            </div>
          </div>
          {/*
            Con sesión el precio grande YA es el de socio (`priceFor` por
            viewer, 14-sep): el aviso de "precio socio · ahorras" solo tiene
            sentido para quien mira sin entrar. A quien entró se le dice
            qué precio está viendo, y a la franquiciataria, su 10 %.
          */}
          {price?.tier === "public" && memberSaving > 0 && memberCents !== undefined && (
            <p className="inline-flex w-fit flex-wrap items-center gap-2 rounded-full bg-ds-lavender-bg px-3 py-1.5 text-[12.5px] text-ds-ink-2">
              <span className="font-semibold text-ds-violet-pop">
                {t("Precio socio")} {formatPrice(memberCents, price.currency, numberLocale)}
              </span>
              <span className="text-ds-ink-3">
                · {t("ahorras {{amount}}", { amount: formatPrice(memberSaving, price.currency, numberLocale) })}
              </span>
            </p>
          )}
          {price?.tier === "member" && price.compareAtCents !== undefined && (
            <p className="inline-flex w-fit rounded-full bg-ds-lavender-bg px-3 py-1.5 text-[12.5px] font-semibold text-ds-violet-pop">
              {t("Tu precio de socio")}
            </p>
          )}
          {price?.tier === "franchisee" && (
            <p className="inline-flex w-fit flex-wrap items-center gap-2 rounded-full bg-ds-lavender-bg px-3 py-1.5 text-[12.5px] text-ds-ink-2">
              <span className="font-semibold text-ds-violet-pop">{t("Tu precio de franquiciatario")}</span>
              <span className="text-ds-ink-3">
                · {t("{{pct}}% menos que el de socio", { pct: price.franchiseeDiscountPercent })}
              </span>
            </p>
          )}
          <ProductPurchase product={product} name={content.name} />
          <ProductDatasheetButton datasheet={product.datasheet} />
          <p className="flex items-center gap-2 text-[12px] text-ds-ink-3">
            <Truck className="h-3.5 w-3.5" aria-hidden="true" />
            {product.shipping?.requiresShipping === false
              ? t("Producto digital: no requiere envío.")
              : t("Envío a todo México y Estados Unidos.")}
          </p>
        </div>
        <ProductFacts category={product.category} presentation={product.presentation} stock={product.stock} />
      </div>
    </aside>
  );
}
